import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

function memberLabel(m) {
  if (!m) return '—';
  const name = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return `${name || '—'} (${m.affiliate_code || '—'})`;
}

const POINT_TYPE_LABELS = {
  CASH: ['Cash Points', '现金积分'],
  PRODUCT: ['Product Points', '产品积分'],
  CREDIT: ['Credit Points', '认购积分'],
  RNR: ['RnR Points', 'RnR 积分'],
};

// Reconciled bonus-system naming (migration 191): PASSIVE_PAYOUT now
// belongs to the cumulative-share dividend pool the UI calls "Passive"
// (formerly labeled Company Pool); the sponsorship-chain star-cascade
// system, which used to sit under that same PASSIVE_PAYOUT type, is now
// STAR_RANKING_PAYOUT / "Team Booster" (团队激励).
const TRANSACTION_TYPE_LABELS = {
  SPONSOR_COMMISSION_L1: ['Direct Referral L1', '直推Lvl1'],
  SPONSOR_COMMISSION_L2: ['Direct Referral L2', '直推Lvl2'],
  PASSIVE_PAYOUT: ['Passive', '静态'],
  STAR_RANKING_PAYOUT: ['Team Booster', '团队激励'],
  TEAM_BONUS_PAYOUT: ['Team Bonus', '团队奖金'],
};

export default function Balances() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const tt = (en, zh) => (isZh ? zh : en);

  const isAdmin = user && (user.role === 'admin' || user.role === 'superadmin');

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');

  const [selectedMember, setSelectedMember] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [packageInfo, setPackageInfo] = useState(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [pointTypeFilter, setPointTypeFilter] = useState('ALL');
  // Admin View shows every ledger row, including internal correction
  // pairs (admin_only_visibility=true, migration 222) -- Member View
  // shows exactly what the member sees in their own app (those rows
  // excluded), so an admin can compare "what actually happened" against
  // "what the member sees" without needing to log in as them.
  const [viewMode, setViewMode] = useState('admin');

  React.useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [authLoading, user, isAdmin, navigate]);

  const runSearch = useCallback(async (q) => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/member-balances/search?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Search failed');
      setResults(data.members || []);
    } catch (err) {
      setError(err.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  }, [token]);

  React.useEffect(() => {
    const timer = setTimeout(() => runSearch(query), 300);
    return () => clearTimeout(timer);
  }, [query, runSearch]);

  const selectMember = async (member) => {
    setSelectedMember(member);
    setLoadingTransactions(true);
    setError('');
    setPointTypeFilter('ALL');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/member-balances/transactions?user_id=${member.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load transactions');
      setTransactions(data.transactions || []);
      setPackageInfo(data.package || null);
    } catch (err) {
      setError(err.message || 'Failed to load transactions');
    } finally {
      setLoadingTransactions(false);
    }
  };

  if (!user || !isAdmin) return null;

  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const viewFilteredTransactions = viewMode === 'member'
    ? transactions.filter((t) => !t.admin_only_visibility)
    : transactions;
  const filteredTransactions = viewFilteredTransactions.filter((t) => pointTypeFilter === 'ALL' || t.point_type === pointTypeFilter);
  const inflowTotal = filteredTransactions.filter((t) => Number(t.amount) > 0).reduce((s, t) => s + Number(t.amount), 0);
  const outflowTotal = filteredTransactions.filter((t) => Number(t.amount) < 0).reduce((s, t) => s + Number(t.amount), 0);

  const cardStyle = { backgroundColor: '#0d2818', borderColor: '#c9a961' };
  const rowStyle = { backgroundColor: '#152214', borderColor: '#3d5a4a' };

  return (
    <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-5xl mx-auto">
        <h1 className="text-headline-lg font-bold mb-6" style={{ color: '#c9a961' }}>
          {tt('Member Balances', '会员余额查询')}
        </h1>

        {error && (
          <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: '#2a0d0d', borderColor: '#c94961', color: '#c94961' }}>
            {error}
          </div>
        )}

        <div className="border rounded-lg p-6 mb-6" style={cardStyle}>
          <input
            type="text"
            placeholder={tt('Search Wellis ID or name...', '搜索 Wellis ID 或姓名...')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full px-3 py-2 text-body-sm rounded border"
            style={{ backgroundColor: '#152214', borderColor: '#3d5a4a', color: '#b8a68f' }}
          />
          {searching && <p className="text-body-xs mt-2" style={{ color: '#999999' }}>{tt('Searching...', '搜索中...')}</p>}
          {!searching && query.trim().length >= 2 && results.length === 0 && (
            <p className="text-body-xs mt-2" style={{ color: '#999999' }}>{tt('No matches.', '没有匹配结果。')}</p>
          )}
          {results.length > 0 && (
            <div className="mt-3 space-y-1 max-h-64 overflow-y-auto">
              {results.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => selectMember(m)}
                  className="w-full text-left p-2 rounded border text-body-sm hover:opacity-80 transition-opacity"
                  style={selectedMember?.id === m.id ? { backgroundColor: '#c9a961', color: '#0d2818', borderColor: '#c9a961' } : { ...rowStyle, color: '#b8a68f' }}
                >
                  {memberLabel(m)}
                </button>
              ))}
            </div>
          )}
        </div>

        {selectedMember && (
          <>
            <div className="border rounded-lg p-6 mb-6" style={cardStyle}>
              <h3 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>
                {memberLabel(selectedMember)}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  [tt('Cash Points', '现金积分'), selectedMember.cash_point_balance],
                  [tt('Product Points', '产品积分'), selectedMember.product_point_balance],
                  [tt('Credit Points', '认购积分'), selectedMember.credit_balance],
                  [tt('RnR Points', 'RnR 积分'), selectedMember.rnr_point_balance],
                ].map(([label, value]) => (
                  <div key={label} className="p-3 rounded border text-center" style={rowStyle}>
                    <div className="text-body-xs" style={{ color: '#999999' }}>{label}</div>
                    <div className="font-bold" style={{ color: '#c9a961' }}>{fmt(value)}</div>
                  </div>
                ))}
              </div>
            </div>

            {packageInfo && (
              <div className="border rounded-lg p-4 mb-6 flex flex-wrap gap-4 items-center" style={cardStyle}>
                <div>
                  <div className="text-body-xs" style={{ color: '#999999' }}>{tt('Package', '套餐')}</div>
                  <div className="font-bold" style={{ color: '#c9a961' }}>{packageInfo.package_code}</div>
                </div>
                <div>
                  <div className="text-body-xs" style={{ color: '#999999' }}>{tt('Credit Point Value', '认购积分价值')}</div>
                  <div className="font-bold" style={{ color: '#c9a961' }}>{fmt(packageInfo.credit_point_value)}</div>
                </div>
                <div>
                  <div className="text-body-xs" style={{ color: '#999999' }}>{tt('Funded Via', '资金来源')}</div>
                  <div className="font-bold" style={{ color: '#c9a961' }}>{packageInfo.funding_source}</div>
                </div>
              </div>
            )}

            <div className="border rounded-lg p-6" style={cardStyle}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="text-headline-sm font-bold" style={{ color: '#c9a961' }}>
                  {tt('Inflows & Outflows', '资金流入/流出明细')}
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex rounded border overflow-hidden" style={{ borderColor: '#3d5a4a' }}>
                    <button
                      type="button"
                      onClick={() => setViewMode('admin')}
                      className="px-3 py-1 text-body-sm font-medium"
                      style={viewMode === 'admin' ? { backgroundColor: '#c9a961', color: '#0d2818' } : { backgroundColor: '#152214', color: '#b8a68f' }}
                    >
                      {tt('Admin View', '管理员视图')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode('member')}
                      className="px-3 py-1 text-body-sm font-medium"
                      style={viewMode === 'member' ? { backgroundColor: '#c9a961', color: '#0d2818' } : { backgroundColor: '#152214', color: '#b8a68f' }}
                    >
                      {tt('Member View', '会员视图')}
                    </button>
                  </div>
                  <select
                    value={pointTypeFilter}
                    onChange={(e) => setPointTypeFilter(e.target.value)}
                    className="px-3 py-1 text-body-sm rounded border"
                    style={{ backgroundColor: '#152214', borderColor: '#3d5a4a', color: '#b8a68f' }}
                  >
                    <option value="ALL">{tt('All point types', '全部积分类型')}</option>
                    {Object.entries(POINT_TYPE_LABELS).map(([key, [en, zh]]) => (
                      <option key={key} value={key}>{tt(en, zh)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <p className="text-body-xs mb-3" style={{ color: '#999999' }}>
                {viewMode === 'admin'
                  ? tt(
                      'Admin View shows every ledger row, including internal correction pairs the member never sees in their own app.',
                      '管理员视图显示所有账本记录，包括会员在自己的应用中看不到的内部更正记录。'
                    )
                  : tt(
                      'Member View shows exactly what this member sees in their own transaction history.',
                      '会员视图显示该会员在自己的交易记录中实际看到的内容。'
                    )}
              </p>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="p-3 rounded border text-center" style={rowStyle}>
                  <div className="text-body-xs" style={{ color: '#999999' }}>{tt('Total Inflow', '总流入')}</div>
                  <div className="font-bold" style={{ color: '#c9a961' }}>+{fmt(inflowTotal)}</div>
                </div>
                <div className="p-3 rounded border text-center" style={rowStyle}>
                  <div className="text-body-xs" style={{ color: '#999999' }}>{tt('Total Outflow', '总流出')}</div>
                  <div className="font-bold" style={{ color: '#e05252' }}>{fmt(outflowTotal)}</div>
                </div>
              </div>

              {loadingTransactions ? (
                <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
              ) : filteredTransactions.length === 0 ? (
                <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('No transactions.', '暂无交易记录。')}</p>
              ) : (
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                    <thead>
                      <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                        <th className="p-2">{tt('Date', '日期')}</th>
                        <th className="p-2">{tt('Type', '类型')}</th>
                        <th className="p-2">{tt('Point Type', '积分类型')}</th>
                        <th className="p-2">{tt('Amount', '金额')}</th>
                        <th className="p-2">{tt('Balance After', '交易后余额')}</th>
                        <th className="p-2">{tt('Package', '套餐')}</th>
                        <th className="p-2">{tt('Notes', '备注')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransactions.map((t) => {
                        const isInflow = Number(t.amount) > 0;
                        const pointLabel = POINT_TYPE_LABELS[t.point_type] ? tt(...POINT_TYPE_LABELS[t.point_type]) : t.point_type;
                        const typeLabel = TRANSACTION_TYPE_LABELS[t.type] ? tt(...TRANSACTION_TYPE_LABELS[t.type]) : t.type;
                        return (
                          <tr key={t.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                            <td className="p-2">{(t.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                            <td className="p-2">{typeLabel}</td>
                            <td className="p-2">{pointLabel}</td>
                            <td className="p-2 font-semibold" style={{ color: isInflow ? '#c9a961' : '#e05252' }}>
                              {isInflow ? '+' : ''}{fmt(t.amount)}
                            </td>
                            <td className="p-2">{fmt(t.balance_after)}</td>
                            <td className="p-2">{t.package_code || '—'}</td>
                            <td className="p-2" style={{ color: '#999999' }}>
                              {t.notes || '—'}
                              {viewMode === 'admin' && t.admin_only_visibility && (
                                <span
                                  className="ml-2 px-1.5 py-0.5 rounded text-body-xs font-medium whitespace-nowrap"
                                  style={{ backgroundColor: 'rgba(201,169,97,0.15)', color: '#c9a961' }}
                                >
                                  {tt('Admin only', '仅管理员可见')}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
